import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  audits as auditsApi,
  checklist as checklistApi,
  templates as templatesApi,
} from "../lib/api";
import { computeScore } from "../lib/score";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import SemaforoBadge from "../components/SemaforoBadge";
import FileUploader from "../components/FileUploader";
import SignaturePad from "../components/SignaturePad";
import { toast } from "sonner";
import {
  Sparkles,
  FileDown,
  Paperclip,
  PenSquare,
  RefreshCw,
  Share2,
  Copy,
  X as XIcon,
  Eye,
  Clock,
} from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import QRCode from "qrcode";
import { localizedCategory, localizedTemplate, localizedQuestion, templateColor } from "../lib/i18nContent";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SummaryPage() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [gerente, setGerente] = useState("");
  const [regional, setRegional] = useState("");
  const [aiSummary, setAiSummary] = useState("");
  const [generating, setGenerating] = useState(false);
  const [managerSig, setManagerSig] = useState("");
  const [regionalSig, setRegionalSig] = useState("");
  const [tplList, setTplList] = useState([]);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareToken, setShareToken] = useState("");
  const [shareExpiresAt, setShareExpiresAt] = useState(null);
  const [shareViews, setShareViews] = useState(0);
  const [shareLastViewed, setShareLastViewed] = useState(null);
  const [expiresInDays, setExpiresInDays] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [sharing, setSharing] = useState(false);

  const buildShareUrl = (tok) => `${window.location.origin}/share/${tok}`;

  useEffect(() => {
    templatesApi.list().then(setTplList).catch(console.error);
  }, []);

  const load = async () => {
    const d = await auditsApi.get(id);
    setData(d);
    setGerente(d.audit.gerente_name || "");
    setRegional(d.audit.regional_name || "");
    setAiSummary(d.audit.ai_summary || "");
    setManagerSig(d.audit.manager_signature || "");
    setRegionalSig(d.audit.regional_signature || "");
    if (d.audit.share_token) {
      setShareToken(d.audit.share_token);
      setShareExpiresAt(d.audit.share_expires_at || null);
      setShareViews(d.audit.share_views || 0);
      setShareLastViewed(d.audit.share_last_viewed_at || null);
      const url = buildShareUrl(d.audit.share_token);
      QRCode.toDataURL(url, { width: 220, margin: 1, color: { dark: "#0B1220", light: "#FFFFFF" } })
        .then(setQrDataUrl)
        .catch(() => {});
    } else {
      setShareToken("");
      setShareExpiresAt(null);
      setShareViews(0);
      setShareLastViewed(null);
      setQrDataUrl("");
    }
    const ck = await checklistApi.list();
    setQuestions(ck);
  };
  useEffect(() => { load(); }, [id]);

  const score = useMemo(() => {
    if (!data) return null;
    return computeScore(data.responses, questions);
  }, [data, questions]);

  if (!data || !score) {
    return <div className="p-10 text-stone-500">{t("common.loading_summary")}</div>;
  }
  const { audit, responses, findings, files } = data;

  // A finding is "active" for THIS report only if the question's CURRENT answer is
  // still a flagged/negative response. Findings for questions later re-answered
  // with a passing value (Sí / Bueno / Aprobado / Cumple / Seguro / N/A, or a
  // data-capture field) are kept in the operational Findings/Follow-up modules but
  // must NOT be reported as current alerts/findings in the audit report/PDF.
  const PASSING_RESPONSES = new Set([
    "yes", "cumple", "good", "pass", "compliant", "safe", "na",
    "text", "number", "media", "signature",
  ]);
  const respByQ = {};
  (responses || []).forEach((r) => { respByQ[r.question_id] = r.response; });
  const activeFindings = (findings || []).filter((f) => {
    const r = respByQ[f.question_id];
    if (!r) return true; // no current answer for this question → keep the finding
    return !PASSING_RESPONSES.has(r);
  });

  const saveSignatures = async () => {
    await auditsApi.patch(id, {
      gerente_name: gerente,
      regional_name: regional,
      signed_at: new Date().toISOString(),
      ai_summary: aiSummary,
      score: score.score,
      score_obtained: score.obtained,
      score_evaluated: score.evaluated,
      status: audit.status,
      manager_signature: managerSig,
      regional_signature: regionalSig,
    });
    toast.success(t("summary.signatures_saved"));
    load();
  };

  const openShare = async () => {
    setSharing(true);
    try {
      const r = await auditsApi.share(id, expiresInDays);
      setShareToken(r.share_token);
      setShareExpiresAt(r.expires_at || null);
      setShareViews(r.share_views || 0);
      setShareLastViewed(r.share_last_viewed_at || null);
      const url = buildShareUrl(r.share_token);
      const png = await QRCode.toDataURL(url, {
        width: 220,
        margin: 1,
        color: { dark: "#0B1220", light: "#FFFFFF" },
      });
      setQrDataUrl(png);
      setShareOpen(true);
    } catch (e) {
      console.error(e);
      toast.error(t("summary.link_error"));
    } finally {
      setSharing(false);
    }
  };

  const refreshShareStats = async () => {
    try {
      const d = await auditsApi.get(id);
      setShareViews(d.audit.share_views || 0);
      setShareLastViewed(d.audit.share_last_viewed_at || null);
      setShareExpiresAt(d.audit.share_expires_at || null);
    } catch (e) {
      // ignore
    }
  };

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(buildShareUrl(shareToken));
      toast.success(t("summary.link_copied"));
    } catch (e) {
      toast.error(t("summary.copy_failed"));
    }
  };

  const revokeShare = async () => {
    if (!window.confirm(t("summary.confirm_revoke"))) return;
    await auditsApi.revokeShare(id);
    setShareToken("");
    setQrDataUrl("");
    setShareExpiresAt(null);
    setShareViews(0);
    setShareLastViewed(null);
    setShareOpen(false);
    toast.success(t("summary.link_revoked"));
    load();
  };

  const generateAi = async () => {
    setGenerating(true);
    try {
      const r = await auditsApi.aiSummary(id, {
        score: score.score,
        semaforo: score.semaforo,
        categories: score.categories,
        findings: activeFindings.map((f) => ({
          severity: f.severity,
          category: f.category,
          description: f.description,
        })),
        store: audit.store_name,
        language: i18n.language,
      });
      setAiSummary(r.summary);
      toast.success(t("summary.ai_success"));
    } catch (e) {
      console.error(e);
      toast.error(t("summary.ai_error"));
    } finally {
      setGenerating(false);
    }
  };

  // ---------- PDF generation ----------
  const generatePdf = async () => {
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const W = doc.internal.pageSize.getWidth();
    const M = 40;
    let y = 50;

    const isEn = String(i18n.language || "").startsWith("en");
    const L = (es, en) => (isEn ? en : es);
    // Build a question-id → checklist doc lookup so we can pull EN translations
    // from `i18n.en` instead of the raw ES snapshot stored in audit_responses.
    const qIndex = {};
    for (const q of questions) { qIndex[q.id] = q; }
    const localizedResp = (r) => {
      const q = qIndex[r.question_id];
      if (q) return localizedQuestion(q, i18n.language);
      return { category: localizedCategory(r.category, i18n.language), question: r.question };
    };
    const auditTemplates = Array.isArray(audit.templates) && audit.templates.length
      ? audit.templates
      : (audit.template ? [audit.template] : []);
    const tplLabel = auditTemplates.length > 1
      ? auditTemplates.map((tn) => localizedTemplate(tn, i18n.language)).join(" + ")
      : (localizedTemplate(auditTemplates[0], i18n.language) || "—");

    // Header
    doc.setFillColor(26, 26, 26);
    doc.rect(0, 0, W, 90, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("Mueblería Canales", M, 38);
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text(L("Reporte de Auditoría Operativa", "Operational Audit Report"), M, 58);
    doc.setFontSize(9);
    doc.text(`${L("Generado", "Generated")}: ${new Date().toLocaleString(isEn ? "en-US" : "es-MX")}`, M, 76);
    y = 110;

    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(audit.store_name, M, y); y += 18;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    doc.text(`${L("Distrito/Región", "District/Region")}: ${audit.distrito || "—"}    ${L("Ciudad", "City")}: ${audit.ciudad || "—"}`, M, y); y += 14;
    doc.text(`${L("Auditor", "Auditor")}: ${audit.auditor_name}    ${L("Estado", "Status")}: ${audit.status === "closed" ? L("Cerrada", "Closed") : L("En curso", "In progress")}`, M, y); y += 14;
    if (audit.audit_number || auditTemplates.length) {
      doc.text(
        `# ${audit.audit_number || "—"}    ${L("Plantilla", "Template")}: ${tplLabel}`,
        M, y,
      );
      y += 14;
    }
    if (audit.manager_name || audit.address) {
      doc.text(
        `${L("Gerente en turno", "Manager on duty")}: ${audit.manager_name || "—"}    ${L("Dirección", "Address")}: ${audit.address || "—"}`,
        M, y,
      );
      y += 14;
    }
    y += 8;

    // Score box
    doc.setDrawColor(229, 229, 229);
    doc.setLineWidth(1);
    doc.roundedRect(M, y, W - M * 2, 70, 6, 6);
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(28);
    doc.text(`${score.score.toFixed(1)}%`, M + 18, y + 42);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    doc.text(L("Calificación final ponderada", "Final weighted score"), M + 18, y + 58);
    const semColor =
      score.semaforo === "verde" ? [22, 163, 74] :
      score.semaforo === "amarillo" ? [180, 83, 9] :
      score.semaforo === "rojo" ? [185, 28, 28] : [120, 113, 108];
    doc.setFillColor(...semColor);
    doc.roundedRect(W - M - 130, y + 18, 110, 32, 4, 4, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(`${L("Semáforo", "Status")} ${L(score.semaforo.toUpperCase(), score.semaforo === "verde" ? "GREEN" : score.semaforo === "amarillo" ? "YELLOW" : score.semaforo === "rojo" ? "RED" : "GRAY")}`, W - M - 75, y + 38, { align: "center" });
    y += 90;

    // AI summary
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(L("Resumen ejecutivo", "Executive summary"), M, y); y += 14;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(40, 40, 40);
    const summaryText = aiSummary || L(
      `Auditoría con calificación ${score.score.toFixed(1)}% (${score.semaforo}). ${activeFindings.length} hallazgo(s) registrado(s).`,
      `Audit scored ${score.score.toFixed(1)}% (${score.semaforo}). ${activeFindings.length} finding(s) recorded.`,
    );
    const lines = doc.splitTextToSize(summaryText, W - M * 2);
    doc.text(lines, M, y);
    y += lines.length * 12 + 14;

    // ===== Executive Snapshot: Top 3 Wins vs Top 3 Concerns =====
    // Wins  : templates with the highest subtotal (evaluated > 0)
    // Concerns : findings with severity "critical"/"high"/"medium", else fallback to
    //            lowest-scoring templates so the block is never empty for a scored audit.
    const wins = [...(score.templates || [])]
      .filter((t) => t.evaluated > 0)
      .sort((a, b) => b.subtotal - a.subtotal)
      .slice(0, 3)
      .map((t) => ({
        title: `${localizedTemplate(t.key, i18n.language)} — ${t.subtotal.toFixed(1)}%`,
        badge: `${t.subtotal.toFixed(0)}%`,
      }));

    const SEVERITY_RANK = { critical: 4, alta: 3, high: 3, media: 2, medium: 2, baja: 1, low: 1 };
    const concerns = (() => {
      const bySeverity = [...activeFindings]
        .filter((f) => f.status !== "done" && f.status !== "cannot_do")
        .sort((a, b) => (SEVERITY_RANK[b.severity] || 0) - (SEVERITY_RANK[a.severity] || 0))
        .slice(0, 3)
        .map((f) => ({
          title: f.description || "—",
          badge: (f.severity || "medium").toUpperCase(),
        }));
      if (bySeverity.length >= 3) return bySeverity;
      // Pad from bottom-scoring templates so leadership always sees something.
      const pad = [...(score.templates || [])]
        .filter((t) => t.evaluated > 0 && t.subtotal < 85)
        .sort((a, b) => a.subtotal - b.subtotal)
        .slice(0, 3 - bySeverity.length)
        .map((t) => ({
          title: `${localizedTemplate(t.key, i18n.language)} — ${t.subtotal.toFixed(1)}%`,
          badge: t.semaforo === "rojo" ? L("ROJO", "RED") : t.semaforo === "amarillo" ? L("AMAR.", "YEL.") : L("BAJO", "LOW"),
        }));
      return [...bySeverity, ...pad];
    })();

    if (wins.length > 0 || concerns.length > 0) {
      const colGap = 14;
      const colW = (W - M * 2 - colGap) / 2;
      const drawColumn = (x, title, items, accent) => {
        // Column header pill
        doc.setFillColor(accent[0], accent[1], accent[2]);
        doc.roundedRect(x, y - 8, colW, 14, 2, 2, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.text(title, x + 6, y + 1);
        let cy = y + 18;
        doc.setTextColor(30, 30, 30);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        if (items.length === 0) {
          doc.setTextColor(120, 120, 120);
          doc.text(L("Sin datos", "No data"), x + 6, cy);
          return cy + 12;
        }
        items.forEach((item, idx) => {
          const rank = `${idx + 1}. `;
          const rankW = doc.getTextWidth(rank);
          doc.setFont("helvetica", "bold");
          doc.text(rank, x + 6, cy);
          doc.setFont("helvetica", "normal");
          const wrapped = doc.splitTextToSize(item.title || "—", colW - 12 - rankW);
          doc.text(wrapped, x + 6 + rankW, cy);
          cy += wrapped.length * 11 + 3;
          if (item.badge) {
            const badgeW = doc.getTextWidth(item.badge) + 8;
            doc.setFillColor(accent[0], accent[1], accent[2]);
            doc.roundedRect(x + 6 + rankW, cy - 7, badgeW, 8, 1.5, 1.5, "F");
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(7);
            doc.text(item.badge, x + 6 + rankW + 4, cy - 1);
            doc.setTextColor(30, 30, 30);
            doc.setFontSize(9);
            cy += 6;
          }
          cy += 4;
        });
        return cy;
      };
      const winsEnd = drawColumn(M, L("Top 3 fortalezas", "Top 3 wins"), wins, [22, 163, 74]);
      const concernsEnd = drawColumn(M + colW + colGap, L("Top 3 alertas", "Top 3 concerns"), concerns, [185, 28, 28]);
      y = Math.max(winsEnd, concernsEnd) + 6;
      // Thin rule separating the snapshot from the legend.
      doc.setDrawColor(230, 230, 230);
      doc.setLineWidth(0.5);
      doc.line(M, y - 2, W - M, y - 2);
      y += 8;
    }

    // Color legend — small horizontal band that decodes the template palette
    // for readers who don't have access to the app. Only rendered when this
    // audit actually touches templates that have colors assigned.
    const legendTemplates = (() => {
      const namesInAudit = new Set(
        questions.map((q) => q.template).filter(Boolean)
      );
      // If the audit doesn't declare templates on the questions, fall back to
      // the audit's declared template list (multi-template audits) or `.template`.
      if (namesInAudit.size === 0) {
        (auditTemplates || []).forEach((n) => namesInAudit.add(n));
      }
      return tplList
        .filter((t) => namesInAudit.has(t.name) && t.color)
        .slice(0, 12); // guard: never blow up a single line beyond ~12 badges
    })();

    if (legendTemplates.length > 0) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);
      doc.text(L("Leyenda de plantillas", "Template legend"), M, y);
      y += 10;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(40, 40, 40);

      // Map template name → precomputed score entry (score.templates uses `key`).
      const tplScoreByName = Object.fromEntries(
        (score.templates || []).map((t) => [t.key, t])
      );
      // Semaforo palette matches SemaforoBadge / hero score box.
      const semaforoColor = (s) =>
        s === "verde" ? [22, 163, 74] :
        s === "amarillo" ? [180, 83, 9] :
        s === "rojo" ? [185, 28, 28] : [120, 113, 108];

      const barW = 70;          // full width of the score bar
      const barH = 3;           // thickness of the bar
      const badgeGapX = 14;     // horizontal gap between badges
      const badgeGapY = 22;     // vertical gap between badge rows
      const dotR = 2.6;
      const gapAfterDot = 5;

      let cx = M;
      let cy = y;

      legendTemplates.forEach((tpl) => {
        const label = localizedTemplate(tpl.name, i18n.language);
        const tScore = tplScoreByName[tpl.name];
        const subtotal = tScore && tScore.evaluated > 0 ? tScore.subtotal : null;
        const scoreText = subtotal === null ? "—" : `${subtotal.toFixed(1)}%`;
        const labelText = `${label}  ${scoreText}`;
        const labelW = doc.getTextWidth(labelText);
        const itemW = Math.max(barW, dotR * 2 + gapAfterDot + labelW);

        // Wrap onto a new line if this badge overflows.
        if (cx + itemW > W - M && cx !== M) {
          cx = M;
          cy += badgeGapY;
        }

        // Line 1: dot + name + score
        const hex = tpl.color || "#94A3B8";
        const r = parseInt(hex.substring(1, 3), 16);
        const g = parseInt(hex.substring(3, 5), 16);
        const b = parseInt(hex.substring(5, 7), 16);
        doc.setFillColor(r, g, b);
        doc.circle(cx + dotR, cy - 2, dotR, "F");
        doc.setTextColor(40, 40, 40);
        doc.text(labelText, cx + dotR * 2 + gapAfterDot, cy);

        // Line 2: score bar (background track + filled portion in semaforo color)
        const trackY = cy + 4;
        doc.setFillColor(230, 230, 230);
        doc.roundedRect(cx, trackY, barW, barH, 1, 1, "F");
        if (subtotal !== null && subtotal > 0) {
          const filled = Math.max(2, (barW * subtotal) / 100);
          const [sr, sg, sb] = semaforoColor(tScore.semaforo);
          doc.setFillColor(sr, sg, sb);
          doc.roundedRect(cx, trackY, filled, barH, 1, 1, "F");
        }

        cx += itemW + badgeGapX;
      });
      y = cy + badgeGapY;
    }

    // Categories table — when multi-template, prefer the per-template breakdown.
    const useTemplateBreakdown = auditTemplates.length > 1 && Array.isArray(score.templates);
    const breakdownData = useTemplateBreakdown ? score.templates : score.categories;
    autoTable(doc, {
      startY: y,
      head: [[
        useTemplateBreakdown ? L("Plantilla", "Template") : L("Categoría", "Category"),
        L("Obtenido", "Obtained"),
        L("Evaluado", "Evaluated"),
        L("Subtotal", "Subtotal"),
        L("Semáforo", "Status"),
      ]],
      body: breakdownData.map((c) => [
        useTemplateBreakdown
          ? localizedTemplate(c.key, i18n.language)
          : localizedCategory(c.category, i18n.language),
        c.obtained.toFixed(1),
        c.evaluated.toFixed(1),
        `${c.subtotal.toFixed(1)}%`,
        c.semaforo,
      ]),
      theme: "grid",
      headStyles: { fillColor: [26, 26, 26], textColor: 255, fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      // Reserve left padding on the first column so the color dot fits.
      columnStyles: { 0: { cellPadding: { left: 12, right: 3, top: 3, bottom: 3 } } },
      margin: { left: M, right: M },
      // Draw a small color bar/dot on the first column matching the template.
      didDrawCell: (hookData) => {
        if (hookData.section !== "body" || hookData.column.index !== 0) return;
        const row = breakdownData[hookData.row.index];
        if (!row) return;
        const tplName = useTemplateBreakdown ? row.key : String(row.category || "").split(" › ")[0];
        const tpl = tplList.find((t) => t.name === tplName);
        const hex = (tpl && tpl.color) || "#94A3B8";
        // Parse "#RRGGBB" to [r,g,b].
        const rgb = [
          parseInt(hex.substring(1, 3), 16),
          parseInt(hex.substring(3, 5), 16),
          parseInt(hex.substring(5, 7), 16),
        ];
        const cx = hookData.cell.x + 5;
        const cy = hookData.cell.y + hookData.cell.height / 2;
        doc.setFillColor(rgb[0], rgb[1], rgb[2]);
        doc.circle(cx, cy, 2.4, "F");
      },
    });
    y = doc.lastAutoTable.finalY + 16;

    // Findings table
    if (activeFindings.length) {
      if (y > 650) { doc.addPage(); y = 50; }
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text(L("Hallazgos", "Findings"), M, y); y += 12;
      const statusLabel = (s) => {
        const mapEs = { to_do: "Hacer", in_progress: "En curso", done: "Completo", cannot_do: "No puedo", Pendiente: "Hacer", "En Proceso": "En curso", Cerrado: "Completo" };
        const mapEn = { to_do: "To do", in_progress: "In progress", done: "Completed", cannot_do: "Can't do", Pendiente: "To do", "En Proceso": "In progress", Cerrado: "Completed" };
        const m = isEn ? mapEn : mapEs;
        return m[s] || s || "—";
      };
      const sevLabel = (s) => {
        if (isEn) return ({ Alta: "High", Media: "Medium", Baja: "Low" })[s] || s;
        return s;
      };
      autoTable(doc, {
        startY: y,
        head: [[L("Categoría", "Category"), L("Descripción", "Description"), L("Severidad", "Severity"), L("Responsable", "Owner"), L("Compromiso", "Due"), L("Estado", "Status")]],
        body: activeFindings.map((f) => [
          localizedCategory(f.category, i18n.language),
          f.description,
          sevLabel(f.severity),
          f.responsible || "—",
          f.commitment_date || "—",
          statusLabel(f.status),
        ]),
        theme: "striped",
        headStyles: { fillColor: [107, 62, 46], textColor: 255, fontSize: 9 },
        bodyStyles: { fontSize: 8 },
        columnStyles: { 1: { cellWidth: 200 } },
        margin: { left: M, right: M },
      });
      y = doc.lastAutoTable.finalY + 16;
    }

    // Detailed responses — when multi-template, group per template with its own section header.
    const responseLabel = (v) => {
      const mapEs = {
        yes: "Sí", cumple: "Sí", no: "No", no_cumple: "No", en_proceso: "En proceso",
        warning: "Advertencia", multa: "Multa", na: "N/A",
        good: "Bueno", fair: "Regular", poor: "Malo",
        pass: "Aprobado", fail: "Reprobado",
        compliant: "Cumple", non_compliant: "No cumple",
        safe: "Seguro", at_risk: "En riesgo",
        text: "Texto", number: "Número", media: "Foto", signature: "Firma",
      };
      const mapEn = {
        yes: "Yes", cumple: "Yes", no: "No", no_cumple: "No", en_proceso: "In progress",
        warning: "Warning", multa: "Fine", na: "N/A",
        good: "Good", fair: "Fair", poor: "Poor",
        pass: "Pass", fail: "Fail",
        compliant: "Compliant", non_compliant: "Non-compliant",
        safe: "Safe", at_risk: "At risk",
        text: "Text", number: "Number", media: "Photo", signature: "Signature",
      };
      const m = isEn ? mapEn : mapEs;
      return m[v] || v || "—";
    };
    const groupsByTemplate = useTemplateBreakdown
      ? auditTemplates.reduce((acc, tpl) => {
          acc[tpl] = responses.filter((r) => (r.template || "Tienda") === tpl);
          return acc;
        }, {})
      : { __all: responses };
    for (const [tplKey, rs] of Object.entries(groupsByTemplate)) {
      if (rs.length === 0) continue;
      if (y > 650) { doc.addPage(); y = 50; }
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      const sectionTitle = tplKey === "__all"
        ? L("Detalle de respuestas", "Response detail")
        : `${L("Detalle de respuestas", "Response detail")} · ${localizedTemplate(tplKey, i18n.language)}`;
      doc.text(sectionTitle, M, y); y += 12;
      autoTable(doc, {
        startY: y,
        head: [[L("Categoría", "Category"), L("Pregunta", "Question"), L("Peso", "Weight"), L("Crit.", "Crit."), L("Respuesta", "Response"), L("Comentario", "Comment")]],
        body: rs.map((r) => {
          const lr = localizedResp(r);
          return [
            lr.category,
            lr.question,
            String(r.weight),
            isEn ? ({ Alta: "High", Media: "Medium", Baja: "Low" })[r.criticality] || r.criticality : r.criticality,
            responseLabel(r.response),
            r.comment || "",
          ];
        }),
        theme: "grid",
        headStyles: { fillColor: [26, 26, 26], textColor: 255, fontSize: 9 },
        bodyStyles: { fontSize: 8 },
        columnStyles: { 1: { cellWidth: 180 }, 5: { cellWidth: 130 } },
        margin: { left: M, right: M },
      });
      y = doc.lastAutoTable.finalY + 16;
    }

    // ===== Visual evidence (photos) =====
    const photoEntries = [];
    for (const r of responses) {
      if (r.photo_url) {
        const lr = localizedResp(r);
        photoEntries.push({ url: r.photo_url, caption: `${lr.category} · ${(lr.question || "").slice(0, 60)}…` });
      }
    }
    for (const f of activeFindings) {
      const list = (f.photos && f.photos.length) ? f.photos : (f.photo_url ? [{ url: f.photo_url }] : []);
      for (const p of list) {
        if (!p.url) continue;
        if (!photoEntries.some((e) => e.url === p.url)) {
          photoEntries.push({ url: p.url, caption: `${L("Hallazgo", "Finding")} · ${localizedCategory(f.category, i18n.language)} · ${(f.description || "").slice(0, 60)}…` });
        }
      }
    }
    if (photoEntries.length) {
      const fetchAsDataUrl = async (url) => {
        try {
          const abs = url.startsWith("http") ? url : `${BACKEND_URL}${url}`;
          const resp = await fetch(abs, { credentials: "include" });
          if (!resp.ok) return null;
          const blob = await resp.blob();
          return await new Promise((resolve, reject) => {
            const fr = new FileReader();
            fr.onload = () => resolve(fr.result);
            fr.onerror = reject;
            fr.readAsDataURL(blob);
          });
        } catch (e) {
          console.error("Photo fetch fail:", e);
          return null;
        }
      };

      doc.addPage();
      y = 50;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.setTextColor(0, 0, 0);
      doc.text(L("Evidencias visuales", "Visual evidence"), M, y); y += 18;

      const cols = 2;
      const gap = 14;
      const cellW = (W - M * 2 - gap * (cols - 1)) / cols;
      const cellH = 200;
      let col = 0;
      for (const entry of photoEntries) {
        if (y + cellH + 30 > 760) {
          doc.addPage();
          y = 50;
          doc.setFont("helvetica", "bold");
          doc.setFontSize(13);
          doc.text(L("Evidencias visuales (cont.)", "Visual evidence (cont.)"), M, y); y += 18;
          col = 0;
        }
        const x = M + col * (cellW + gap);
        const dataUrl = await fetchAsDataUrl(entry.url);
        try {
          if (dataUrl) {
            const fmt = dataUrl.startsWith("data:image/png") ? "PNG" : "JPEG";
            doc.addImage(dataUrl, fmt, x, y, cellW, cellH, undefined, "FAST");
          } else {
            doc.setDrawColor(229, 229, 229);
            doc.rect(x, y, cellW, cellH);
            doc.setFontSize(9);
            doc.setTextColor(150, 150, 150);
            doc.text("(no se pudo cargar la imagen)", x + 8, y + cellH / 2);
          }
        } catch (e) {
          console.error("addImage fail:", e);
        }
        doc.setFontSize(8);
        doc.setTextColor(80, 80, 80);
        const caption = doc.splitTextToSize(entry.caption, cellW);
        doc.text(caption, x, y + cellH + 12);
        if (col === cols - 1) {
          y += cellH + 36;
          col = 0;
        } else {
          col += 1;
        }
      }
      if (col !== 0) y += cellH + 36;
    }

    // Attachments list
    if (files && files.length) {
      if (y > 650) { doc.addPage(); y = 50; }
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text(L("Archivos adjuntos", "Attachments"), M, y); y += 12;
      autoTable(doc, {
        startY: y,
        head: [[L("Archivo", "File"), L("Tipo", "Type"), L("Tamaño (KB)", "Size (KB)"), L("Fecha", "Date")]],
        body: files.map((f) => [
          f.original_filename,
          f.kind,
          (f.size / 1024).toFixed(1),
          (f.created_at || "").slice(0, 10),
        ]),
        theme: "grid",
        headStyles: { fillColor: [26, 26, 26], textColor: 255, fontSize: 9 },
        bodyStyles: { fontSize: 8 },
        margin: { left: M, right: M },
      });
      y = doc.lastAutoTable.finalY + 16;
    }

    // Signatures
    if (y > 680) { doc.addPage(); y = 50; }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(L("Firmas", "Signatures"), M, y); y += 16;
    doc.setLineWidth(0.5);
    doc.setDrawColor(180, 180, 180);

    const colW = (W - M * 2 - 20) / 2;
    // Embed signature images (data URLs) above the line if present.
    const sigImgH = 42;
    if (managerSig) {
      try { doc.addImage(managerSig, "PNG", M, y - 6, colW, sigImgH); } catch (err) { /* invalid image, skip */ }
    }
    if (regionalSig) {
      try { doc.addImage(regionalSig, "PNG", M + colW + 20, y - 6, colW, sigImgH); } catch (err) { /* invalid image, skip */ }
    }
    doc.line(M, y + 30, M + colW, y + 30);
    doc.line(M + colW + 20, y + 30, W - M, y + 30);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(gerente || L("Gerente de tienda", "Store manager"), M, y + 44);
    doc.setTextColor(120, 120, 120);
    doc.text(L("Gerente de tienda", "Store manager"), M, y + 58);
    doc.setTextColor(0, 0, 0);
    doc.text(regional || L("Regional / Auditor", "Regional / Auditor"), M + colW + 20, y + 44);
    doc.setTextColor(120, 120, 120);
    doc.text(L("Regional / Auditor", "Regional / Auditor"), M + colW + 20, y + 58);

    doc.setTextColor(0, 0, 0);
    const sealedAt = audit.signed_at || new Date().toISOString();
    doc.setFontSize(8);
    doc.text(`${L("Fecha firma", "Signed at")}: ${new Date(sealedAt).toLocaleString(isEn ? "en-US" : "es-MX")}`, M, y + 80);

    // QR code with public share link (if available)
    if (shareToken) {
      try {
        const url = buildShareUrl(shareToken);
        const qr = await QRCode.toDataURL(url, {
          width: 320,
          margin: 1,
          color: { dark: "#0B1220", light: "#FFFFFF" },
        });
        const qrSize = 90;
        const qrX = W - M - qrSize;
        const qrY = y + 8;
        doc.addImage(qr, "PNG", qrX, qrY, qrSize, qrSize);
        doc.setFontSize(8);
        doc.setTextColor(80, 80, 80);
        doc.text(L("Escanea para ver el reporte en línea", "Scan to view the report online"), qrX + qrSize / 2, qrY + qrSize + 12, { align: "center" });
      } catch (e) {
        // ignore QR failure
      }
    }

    const fname = `Auditoria_${audit.store_name.replace(/\s+/g, "_")}_${(audit.started_at || "").slice(0, 10)}.pdf`;
    doc.save(fname);
    toast.success(L("PDF generado", "PDF generated"));
  };

  return (
    <div className="p-6 lg:p-10 max-w-[1500px] mx-auto" data-testid="summary-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="canales-overline">{t("summary.title")}</div>
          <h1 className="font-display text-4xl md:text-5xl mt-1">{audit.store_name}</h1>
          <p className="text-stone-600 mt-2 text-sm">
            {audit.distrito} · {audit.ciudad} · {t("audit.auditor_label")}: {audit.auditor_name}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <SemaforoBadge score={score.score} semaforo={score.semaforo} size="lg" />
          <Button
            onClick={() => { refreshShareStats(); setShareOpen(true); }}
            disabled={sharing}
            variant="outline"
            className="gap-2 rounded-full"
            data-testid="share-button"
          >
            <Share2 size={16} /> {shareToken ? t("common.share") : t("common.share")}
          </Button>
          <Button onClick={generatePdf} className="brand-button-accent gap-2" data-testid="generate-pdf-button">
            <FileDown size={16} /> {t("summary.download_pdf")}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
        <Card className="canales-card p-6 lg:col-span-2">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="canales-overline">{t("summary.ai_title")}</div>
              <h3 className="font-display text-xl mt-1">{t("summary.ai_subtitle")}</h3>
            </div>
            <Button variant="outline" onClick={generateAi} disabled={generating} className="gap-2" data-testid="generate-ai-button">
              {generating ? <RefreshCw size={16} className="animate-spin" /> : <Sparkles size={16} />}
              {generating ? t("common.loading") : aiSummary ? t("summary.regenerate_ai") : t("summary.generate_ai")}
            </Button>
          </div>
          <Textarea
            value={aiSummary}
            onChange={(e) => setAiSummary(e.target.value)}
            placeholder={t("summary.ai_placeholder")}
            className="mt-4 min-h-[160px]"
            data-testid="ai-summary-textarea"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            <div>
              <label className="canales-overline">{t("summary.gerente_name")}</label>
              <Input
                value={gerente}
                onChange={(e) => setGerente(e.target.value)}
                className="mt-1"
                placeholder={t("common.name")}
                data-testid="gerente-input"
              />
            </div>
            <div>
              <label className="canales-overline">{t("summary.regional_name")}</label>
              <Input
                value={regional}
                onChange={(e) => setRegional(e.target.value)}
                className="mt-1"
                placeholder={t("common.name")}
                data-testid="regional-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <SignaturePad
              value={managerSig}
              onChange={setManagerSig}
              label={t("summary.gerente_signature", "Firma del gerente")}
              testid="manager-signature"
            />
            <SignaturePad
              value={regionalSig}
              onChange={setRegionalSig}
              label={t("summary.regional_signature", "Firma regional / auditor")}
              testid="regional-signature"
            />
          </div>

          <div className="flex items-center gap-3 mt-4 flex-wrap">
            <Button onClick={saveSignatures} className="brand-button gap-2" data-testid="save-signatures-button">
              <PenSquare size={16} /> {t("summary.save_signatures")}
            </Button>
            <FileUploader auditId={id} kind="attachment" multiple onUploaded={() => load()} testid="summary-attach" label={t("common.share")} />
          </div>
        </Card>

        <Card className="canales-card p-6">
          <div className="canales-overline">{t("summary.category_subtotals")}</div>
          <h3 className="font-display text-xl mt-1">{t("dashboard.semaphore_subtitle")}</h3>
          <ul className="mt-4 space-y-2.5" data-testid="category-subtotals">
            {score.categories.map((c) => {
              const parts = String(c.category || "").split(" › ");
              const dotColor = parts.length === 2 ? templateColor(parts[0], tplList) : null;
              return (
              <li key={c.category} className="flex items-center justify-between text-sm">
                <span className="text-stone-700 flex items-center gap-2">
                  {dotColor && (
                    <span
                      className="inline-block w-2 h-2 rounded-full ring-1 ring-white/60 shrink-0"
                      style={{ backgroundColor: dotColor }}
                      aria-hidden
                    />
                  )}
                  {localizedCategory(c.category, i18n.language)}
                </span>
                <span className="flex items-center gap-2">
                  <span className="text-stone-500 text-xs">
                    {c.obtained.toFixed(1)}/{c.evaluated.toFixed(1)}
                  </span>
                  <SemaforoBadge score={c.subtotal} semaforo={c.semaforo} />
                </span>
              </li>
              );
            })}
            {score.categories.length === 0 && (
              <li className="text-sm text-stone-500">{t("common.empty")}</li>
            )}
          </ul>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <Card className="canales-card p-6">
          <div className="canales-overline">{t("findings.title")}</div>
          <h3 className="font-display text-xl mt-1">{activeFindings.length}</h3>
          {activeFindings.length === 0 ? (
            <p className="text-sm text-stone-500 mt-4">{t("findings.no_findings")}</p>
          ) : (
            <ul className="divide-y divide-stone-200 mt-3">
              {activeFindings.map((f) => (
                <li key={f.id} className="py-3 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-medium">{f.description}</span>
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${
                        f.severity === "Alta"
                          ? "bg-red-50 text-red-700 ring-red-600/20"
                          : f.severity === "Media"
                          ? "bg-amber-50 text-amber-700 ring-amber-600/20"
                          : "bg-stone-100 text-stone-700 ring-stone-400/20"
                      }`}
                    >
                      {t(`severity.${f.severity}`, f.severity)}
                    </span>
                  </div>
                  <div className="text-xs text-stone-500 mt-1">
                    {localizedCategory(f.category, i18n.language)} · {t(`finding_status.${f.status}`, f.status)} {f.responsible ? `· ${f.responsible}` : ""}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="canales-card p-6">
          <div className="canales-overline">{t("summary.attachments")}</div>
          <h3 className="font-display text-xl mt-1">{files.length}</h3>
          {files.length === 0 ? (
            <p className="text-sm text-stone-500 mt-4">{t("common.empty")}</p>
          ) : (
            <ul className="space-y-2 mt-3" data-testid="attachments-list">
              {files.map((f) => (
                <li key={f.id} className="flex items-center justify-between text-sm">
                  <a
                    href={f.url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2 hover:underline truncate"
                  >
                    <Paperclip size={14} />
                    <span className="truncate">{f.original_filename}</span>
                  </a>
                  <span className="text-xs text-stone-500 shrink-0 ml-3">
                    {(f.size / 1024).toFixed(0)} KB · {f.kind}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* Share dialog */}
      <Dialog open={shareOpen} onOpenChange={(v) => { setShareOpen(v); if (v) refreshShareStats(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("summary.share_report")}</DialogTitle>
            <DialogDescription>
              {t("summary.share_desc")}
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center justify-center mt-4">
            {qrDataUrl ? (
              <img src={qrDataUrl} alt="QR del enlace" className="w-52 h-52 rounded-xl border border-stone-200" data-testid="share-qr" />
            ) : (
              <div className="w-52 h-52 rounded-xl bg-stone-50 border border-dashed border-stone-300 grid place-items-center text-stone-400 text-xs px-4 text-center whitespace-pre-line">
                {t("summary.no_link")}
              </div>
            )}
          </div>

          {shareToken && (
            <div className="mt-4 flex items-center gap-2 bg-stone-50 rounded-full p-1.5 pl-4 border border-stone-200">
              <code className="text-xs flex-1 truncate text-stone-700" data-testid="share-link">
                {buildShareUrl(shareToken)}
              </code>
              <Button size="sm" onClick={copyShareLink} className="brand-button h-8 gap-1.5" data-testid="copy-share-button">
                <Copy size={13} /> Copiar
              </Button>
            </div>
          )}

          {/* Stats */}
          {shareToken && (
            <div className="mt-4 grid grid-cols-2 gap-2" data-testid="share-stats">
              <div className="rounded-xl border border-stone-200 p-3">
                <div className="canales-overline flex items-center gap-1.5"><Eye size={11} /> {t("summary.views")}</div>
                <div className="font-display text-2xl mt-0.5" data-testid="share-views">{shareViews}</div>
                <div className="text-[11px] text-stone-500 mt-0.5">
                  {shareLastViewed
                    ? t("summary.last_viewed", { when: new Date(shareLastViewed).toLocaleString() })
                    : t("summary.not_viewed")}
                </div>
              </div>
              <div className="rounded-xl border border-stone-200 p-3">
                <div className="canales-overline flex items-center gap-1.5"><Clock size={11} /> {t("summary.expiration")}</div>
                <div className="font-display text-base mt-0.5" data-testid="share-expires">
                  {shareExpiresAt
                    ? new Date(shareExpiresAt).toLocaleDateString()
                    : t("summary.no_expiry")}
                </div>
                {shareExpiresAt && (
                  <div className="text-[11px] text-stone-500 mt-0.5">
                    {t("summary.in_n_days", { n: Math.max(0, Math.ceil((new Date(shareExpiresAt) - Date.now()) / 86400000)) })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Expiration selector */}
          <div className="mt-4">
            <div className="canales-overline mb-2">{t("summary.expiration")}</div>
            <div className="flex flex-wrap gap-2" data-testid="expiration-options">
              {[
                { v: null, label: t("summary.no_expiry") },
                { v: 7, label: t("summary.days_7") },
                { v: 30, label: t("summary.days_30") },
                { v: 90, label: t("summary.days_90") },
              ].map((opt) => (
                <button
                  key={String(opt.v)}
                  onClick={() => setExpiresInDays(opt.v)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${
                    expiresInDays === opt.v
                      ? "bg-[#1E3A8A] text-white"
                      : "bg-white border border-stone-200 text-stone-700 hover:border-stone-300"
                  }`}
                  data-testid={`expires-${opt.v ?? "none"}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <Button
                size="sm"
                onClick={openShare}
                disabled={sharing}
                className="brand-button gap-2 h-9"
                data-testid="apply-expiration-button"
              >
                <RefreshCw size={13} /> {shareToken ? t("summary.update_expiry") : t("summary.generate_link")}
              </Button>
            </div>
          </div>

          <div className="mt-3 text-xs text-stone-500">
            {t("summary.tip_qr")}
          </div>

          <div className="mt-4 flex items-center justify-between">
            {shareToken ? (
              <Button
                variant="outline"
                onClick={revokeShare}
                className="gap-2 text-red-600 hover:text-red-700 border-red-200"
                data-testid="revoke-share-button"
              >
                <XIcon size={14} /> {t("summary.revoke")}
              </Button>
            ) : (
              <span />
            )}
            <Button onClick={() => setShareOpen(false)} variant="ghost">{t("common.close")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
